import { useQuery } from '@tanstack/react-query';
import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';

import { apiFetch } from '../lib/api.ts';
import { dateOnly, dateTime, money, num } from '../lib/format.ts';

interface OverviewResponse {
  asOf: string;
  range: { from: string; to: string };
  invoiceCountInRange: number;
  revenueInRange: number;
  arOutstanding: number;
  apOutstanding: number;
  topCustomersInRange: Array<{
    customerNo: string;
    customerName: string | null;
    invoiceCount: number;
    revenue: number;
  }>;
  flaggedSkus: Array<{
    productId: string;
    name: string;
    location: string | null;
  }>;
}

type PresetKey = '7d' | '30d' | 'last-month' | 'last-quarter' | 'last-year' | 'custom';

interface DateRange {
  from: string; // YYYY-MM-DD
  to: string;   // YYYY-MM-DD
}

const PRESET_LABELS: Record<PresetKey, string> = {
  '7d': 'Last 7 days',
  '30d': 'Last 30 days',
  'last-month': 'Last month',
  'last-quarter': 'Last quarter',
  'last-year': 'Last year',
  'custom': 'Custom',
};

function ymd(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${dd}`;
}

function presetRange(key: Exclude<PresetKey, 'custom'>): DateRange {
  const today = new Date();
  switch (key) {
    case '7d': {
      const from = new Date(today);
      from.setDate(from.getDate() - 6);
      return { from: ymd(from), to: ymd(today) };
    }
    case '30d': {
      const from = new Date(today);
      from.setDate(from.getDate() - 29);
      return { from: ymd(from), to: ymd(today) };
    }
    case 'last-month': {
      // First & last day of the previous calendar month.
      const first = new Date(today.getFullYear(), today.getMonth() - 1, 1);
      const last = new Date(today.getFullYear(), today.getMonth(), 0);
      return { from: ymd(first), to: ymd(last) };
    }
    case 'last-quarter': {
      // Q is 3-month block ending the previous calendar month.
      const m = today.getMonth();
      const currentQuarterStartMonth = Math.floor(m / 3) * 3;
      const lastQuarterEnd = new Date(today.getFullYear(), currentQuarterStartMonth, 0);
      const lastQuarterStart = new Date(
        lastQuarterEnd.getFullYear(),
        lastQuarterEnd.getMonth() - 2,
        1,
      );
      return { from: ymd(lastQuarterStart), to: ymd(lastQuarterEnd) };
    }
    case 'last-year': {
      const y = today.getFullYear() - 1;
      return { from: `${y}-01-01`, to: `${y}-12-31` };
    }
  }
}

export function OverviewPage() {
  const [preset, setPreset] = useState<PresetKey>('30d');
  const [customRange, setCustomRange] = useState<DateRange>(() => presetRange('30d'));

  const range: DateRange = useMemo(
    () => (preset === 'custom' ? customRange : presetRange(preset)),
    [preset, customRange],
  );

  const { data, isLoading, error } = useQuery({
    queryKey: ['overview', range.from, range.to],
    queryFn: () =>
      apiFetch<OverviewResponse>(
        `/api/dashboard/overview?from=${range.from}&to=${range.to}`,
      ),
    staleTime: 60_000,
  });

  return (
    <>
      <header className="main-header">
        <h1 className="main-title">Overview</h1>
        <span className="main-subtitle">
          {data ? `As of ${dateTime(data.asOf)}` : ''}
        </span>
      </header>

      <RangePicker
        preset={preset}
        custom={customRange}
        onPresetChange={setPreset}
        onCustomChange={setCustomRange}
      />

      {isLoading && <div className="loading">Loading…</div>}
      {error && <div className="error">{(error as Error).message}</div>}

      {data && (
        <>
          <div className="kpi-grid">
            <KPI
              label={`Invoices · ${dateOnly(range.from)} – ${dateOnly(range.to)}`}
              value={num(data.invoiceCountInRange)}
            />
            <KPI
              label={`Revenue · ${dateOnly(range.from)} – ${dateOnly(range.to)}`}
              value={money(data.revenueInRange)}
            />
            <KPI label="AR outstanding" value={money(data.arOutstanding)} sub="open invoices" />
            <KPI label="AP outstanding" value={money(data.apOutstanding)} sub="open purchases" />
          </div>

          <div className="two-col">
            <section className="panel">
              <header className="panel-header">
                <h2 className="panel-title">Top customers · selected range</h2>
                <span className="panel-subtitle">by invoice value</span>
              </header>
              {data.topCustomersInRange.length === 0 ? (
                <div className="empty">No invoices in the selected range.</div>
              ) : (
                <table className="table">
                  <thead>
                    <tr>
                      <th>Customer</th>
                      <th className="num">Invoices</th>
                      <th className="num">Revenue</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.topCustomersInRange.map((c) => (
                      <tr key={c.customerNo}>
                        <td>
                          <Link to={`/customers/${encodeURIComponent(c.customerNo)}`}>
                            {c.customerName ?? c.customerNo}
                          </Link>
                          <div className="mono" style={{ color: 'var(--text-dim)', fontSize: 11 }}>
                            {c.customerNo}
                          </div>
                        </td>
                        <td className="num">{num(c.invoiceCount)}</td>
                        <td className="num">{money(c.revenue)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </section>

            <section className="panel">
              <header className="panel-header">
                <h2 className="panel-title">Data quality flags</h2>
                <span className="panel-subtitle">catch-all SKUs</span>
              </header>
              {data.flaggedSkus.length === 0 ? (
                <div className="empty">No flagged SKUs — clean.</div>
              ) : (
                <table className="table">
                  <thead>
                    <tr>
                      <th>Product</th>
                      <th>Location</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.flaggedSkus.map((s) => (
                      <tr key={s.productId}>
                        <td>
                          <Link to={`/skus/${encodeURIComponent(s.productId)}`}>
                            <span className="mono">{s.productId}</span>
                          </Link>
                          <div style={{ color: 'var(--text-dim)', fontSize: 11 }}>
                            {s.name || '—'}
                          </div>
                        </td>
                        <td className="mono">{s.location ?? '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </section>
          </div>
        </>
      )}
    </>
  );
}

function RangePicker({
  preset,
  custom,
  onPresetChange,
  onCustomChange,
}: {
  preset: PresetKey;
  custom: DateRange;
  onPresetChange: (p: PresetKey) => void;
  onCustomChange: (r: DateRange) => void;
}) {
  return (
    <section className="panel" style={{ padding: '0.75rem 1rem' }}>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.4rem', alignItems: 'center' }}>
        {(Object.keys(PRESET_LABELS) as PresetKey[]).map((k) => (
          <button
            key={k}
            type="button"
            onClick={() => onPresetChange(k)}
            style={{
              background: preset === k ? 'var(--accent)' : 'var(--surface-2)',
              color: preset === k ? 'var(--bg)' : 'var(--text)',
              border: '1px solid var(--border)',
              padding: '0.4rem 0.75rem',
              cursor: 'pointer',
              fontSize: 12,
              borderRadius: 2,
              fontWeight: preset === k ? 600 : 400,
            }}
          >
            {PRESET_LABELS[k]}
          </button>
        ))}

        {preset === 'custom' && (
          <span style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', marginLeft: '0.5rem' }}>
            <label style={{ fontSize: 11, color: 'var(--text-muted)' }}>From</label>
            <input
              type="date"
              value={custom.from}
              max={custom.to}
              onChange={(e) => onCustomChange({ ...custom, from: e.target.value })}
              style={{ padding: '0.3rem' }}
            />
            <label style={{ fontSize: 11, color: 'var(--text-muted)' }}>To</label>
            <input
              type="date"
              value={custom.to}
              min={custom.from}
              onChange={(e) => onCustomChange({ ...custom, to: e.target.value })}
              style={{ padding: '0.3rem' }}
            />
          </span>
        )}
      </div>
    </section>
  );
}

function KPI({ label, value, sub }: { label: string; value: string; sub?: string }) {
  return (
    <div className="kpi-card">
      <div className="kpi-label">{label}</div>
      <div className="kpi-value">{value}</div>
      {sub && <div className="kpi-sub">{sub}</div>}
    </div>
  );
}
